// RamsRuntimeForm — the actual Phase 3 questionnaire-answering surface,
// shared verbatim by the public secure-link page and the authenticated
// client-portal/internal completion flow. Callers just supply the 3 API
// functions (save/submit/upload) — everything else (autosave, conditional
// show/require, section layout, progress) lives here once.
import { useEffect, useRef, useState } from "react";
import { CheckCircle2, Loader2, ShieldCheck } from "lucide-react";
import RamsRuntimeQuestion from "./RamsRuntimeQuestion";

function ruleMatches(rule, answers) {
    const val = answers[rule.source_key];
    switch (rule.operator) {
        case "is_yes": return val === "Yes" || val === true;
        case "is_no": return val === "No" || val === false;
        case "not_empty": return val !== undefined && val !== null && val !== "";
        case "not_equals": return String(val ?? "") !== String(rule.value ?? "");
        case "in": return (rule.value || "").split(",").map((v) => v.trim()).includes(String(val ?? ""));
        default: return String(val ?? "") === String(rule.value ?? "");
    }
}
function isVisible(q, answers) {
    const rules = (q.conditional_rules || []).filter((r) => r.action === "show");
    return rules.length === 0 || rules.every((r) => ruleMatches(r, answers));
}
function isRequired(q, answers) {
    if (q.required) return true;
    const rules = (q.conditional_rules || []).filter((r) => r.action === "require");
    return rules.length > 0 && rules.every((r) => ruleMatches(r, answers));
}

export default function RamsRuntimeForm({
    sections, initialAnswers, initialFiles, prefill, editable, requestMeta, aiPrefilledFields,
    guestMode, saveApi, submitApi, uploadApi, brand = "#C75A1B", onSubmitted,
}) {
    const [answers, setAnswers] = useState({ ...(prefill || {}), ...(initialAnswers || {}) });
    const [aiFields, setAiFields] = useState(() => new Set(aiPrefilledFields || []));
    const [files, setFiles] = useState(initialFiles || []);
    const [saveState, setSaveState] = useState("idle"); // idle | saving | saved
    const [pct, setPct] = useState(0);
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [error, setError] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);
    const mounted = useRef(false);

    const set = (key, val) => {
        setAnswers((a) => ({ ...a, [key]: val }));
        setAiFields((s) => { if (!s.has(key)) return s; const next = new Set(s); next.delete(key); return next; });
    };

    useEffect(() => {
        if (!mounted.current) { mounted.current = true; return; }
        if (!editable) return;
        setSaveState("saving");
        const t = setTimeout(() => {
            saveApi({ answers, completed_by_name: name || undefined, completed_by_email: email || undefined })
                .then((r) => { setPct(r?.percentage_completed ?? pct); setSaveState("saved"); })
                .catch(() => setSaveState("idle"));
        }, 1200);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [answers]);

    const upload = async (fieldKey, file) => {
        try {
            const r = await uploadApi(fieldKey, file);
            const rec = { id: r.file_id, field_key: fieldKey, filename: r.filename };
            setFiles((f) => [...f, rec]);
            set(fieldKey, [...(Array.isArray(answers[fieldKey]) ? answers[fieldKey] : []), r.file_id]);
        } catch (e) { setError(e.response?.data?.detail || "Upload failed"); }
    };

    const submit = async () => {
        setError(null);
        if (guestMode && !name.trim()) { setError("Please enter your name to submit."); return; }
        setSubmitting(true);
        try {
            await submitApi({ answers, completed_by_name: name.trim() || undefined, completed_by_email: email.trim() || undefined });
            setDone(true);
            onSubmitted?.();
        } catch (e) { setError(e.response?.data?.detail || "Could not submit — please check required fields."); }
        setSubmitting(false);
    };

    if (done) return (
        <div className="text-center py-10" data-testid="rams-runtime-done">
            <CheckCircle2 className="mx-auto mb-3" size={44} style={{ color: brand }} />
            <h3 className="text-lg font-bold text-[#1F1F1F]">Thank you{name ? `, ${name.split(" ")[0]}` : ""}</h3>
            <p className="text-[#6F6A64] mt-1 text-[13px]">This RAMS questionnaire has been submitted to Priority Risk for review.</p>
        </div>
    );

    return (
        <div data-testid="rams-runtime-form" className="space-y-6">
            {requestMeta?.message && (
                <div className="p-3 rounded-lg bg-[#F5F3F0] text-[12.5px] text-[#6F6A64]">{requestMeta.message}</div>
            )}
            {guestMode && (
                <div className="grid grid-cols-2 gap-2 p-3 rounded-lg border border-[#E5E0DA]">
                    <div><label className="block text-[11px] font-semibold text-[#6F6A64] mb-1">Your name *</label>
                        <input data-testid="rams-runtime-name" value={name} onChange={(e) => setName(e.target.value)} className="w-full h-9 px-3 rounded-lg border border-[#E5E0DA] text-sm" /></div>
                    <div><label className="block text-[11px] font-semibold text-[#6F6A64] mb-1">Email</label>
                        <input data-testid="rams-runtime-email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full h-9 px-3 rounded-lg border border-[#E5E0DA] text-sm" /></div>
                </div>
            )}
            {editable && (
                <div className="flex items-center justify-between text-[11px] text-[#9A968F]">
                    <span className="flex items-center gap-1"><ShieldCheck size={11} /> Autosaves as you go — {pct}% complete</span>
                    <span>{saveState === "saving" ? <><Loader2 size={11} className="inline animate-spin" /> Saving…</> : saveState === "saved" ? "Saved" : ""}</span>
                </div>
            )}
            {(sections || []).map((sec) => {
                const qs = (sec.questions || []).filter((q) => isVisible(q, answers));
                if (qs.length === 0) return null;
                return (
                    <div key={sec.id} data-testid={`rams-runtime-section-${sec.id}`}>
                        <h4 className="text-sm font-bold text-[#1F1F1F] mb-2 pb-1.5 border-b border-[#F0EDE8]">{sec.title}</h4>
                        <div className="space-y-3.5">
                            {qs.map((q) => (
                                <RamsRuntimeQuestion key={q.id} question={q} required={isRequired(q, answers)}
                                    value={answers[q.field_key]} onChange={(v) => set(q.field_key, v)}
                                    files={files} onUpload={upload} disabled={!editable} aiSuggested={aiFields.has(q.field_key)}
                                    testid={`rams-runtime-q-${q.field_key}`} />
                            ))}
                        </div>
                    </div>
                );
            })}
            {error && <p className="text-[12.5px] text-red-600" data-testid="rams-runtime-error">{error}</p>}
            {editable && (
                <button data-testid="rams-runtime-submit" onClick={submit} disabled={submitting}
                    className="w-full h-11 rounded-lg font-semibold text-white disabled:opacity-60"
                    style={{ background: brand }}>
                    {submitting ? <Loader2 size={16} className="inline animate-spin" /> : "Submit RAMS questionnaire"}
                </button>
            )}
        </div>
    );
}
